
export default function VisioneFuturoSection() {
  const visionPoints = [
    {
      icon: 'ri-building-4-line',
      title: 'Città Viventi',
      desc: 'Entro il 2050, le nostre città diventeranno ecosistemi viventi. Edifici che producono più energia di quanta ne consumano, facciate che purificano l\'aria e tetti che ospitano biodiversità. La città del futuro non sarà grigia, ma verde.',
    },
    {
      icon: 'ri-recycle-line',
      title: 'Economia Circolare Edilizia',
      desc: 'Ogni materiale avrà una seconda vita. Il calcestruzzo diventerà un ricordo del passato, sostituito da biomateriali rigenerativi. Gli edifici saranno progettati per essere smontati e riutilizzati, eliminando il concetto stesso di rifiuto edilizio.',
    },
    {
      icon: 'ri-temp-cold-line',
      title: 'Zero Emissioni Reali',
      desc: 'Non basterà più ridurre le emissioni: dovremo azzerarle. Attraverso l\'integrazione di sistemi geotermici, fotovoltaici organici e isolamenti in fibra vegetale, ogni edificio diventerà un alleato nella lotta al cambiamento climatico.',
    },
    {
      icon: 'ri-water-flash-line',
      title: 'Autosufficienza Idrica',
      desc: 'Sistemi di raccolta, filtraggio e riutilizzo delle acque piovane integrati in ogni progetto. L\'acqua non sarà più sprecata ma valorizzata come la risorsa preziosa che è, con cicli chiusi che imitano i processi naturali.',
    },
  ];

  const milestones = [
    { year: '2030', text: 'Tutti i nuovi edifici a energia quasi zero in Europa' },
    { year: '2035', text: 'Biomateriali superano il 50% nell\'edilizia sostenibile' },
    { year: '2040', text: 'Città carbon-negative diventano realtà concreta' },
    { year: '2050', text: 'Architettura rigenerativa come standard globale' },
  ];

  return (
    <section id="visione" className="py-16 md:py-32 bg-[#f5f2ee] relative overflow-hidden">
      {/* Subtle decorative element */}
      <div className="absolute top-0 right-0 w-[300px] md:w-[500px] h-[300px] md:h-[500px] rounded-full bg-[#6b8e7f]/5 -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-[200px] md:w-[300px] h-[200px] md:h-[300px] rounded-full bg-[#6b8e7f]/3 translate-y-1/2 -translate-x-1/2"></div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 md:mb-20">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-[1px] bg-[#6b8e7f]"></div>
            <span className="font-montserrat text-[10px] md:text-[11px] uppercase tracking-[3px] md:tracking-[4px] text-[#6b8e7f]">
              La Mia Visione
            </span>
            <div className="w-8 h-[1px] bg-[#6b8e7f]"></div>
          </div>
          <h2 className="font-playfair text-[32px] md:text-[52px] font-bold text-[#2d3e40] leading-[1.15]">
            Il Futuro che{' '}
            <em className="text-[#6b8e7f] not-italic">Immagino</em>
          </h2>
          <p className="font-montserrat text-[13px] md:text-[15px] text-[#2d3e40]/60 mt-4 md:mt-6 max-w-[650px] mx-auto leading-[1.8]">
            Credo fermamente che l&apos;architettura sostenibile non sia un&apos;opzione, ma l&apos;unica strada possibile. Il futuro del nostro pianeta dipende dalle scelte che facciamo oggi nel costruire.
          </p>
        </div>

        {/* Main Content: Image + Text */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start mb-16 md:mb-24">
          {/* Left: Large Image */}
          <div className="w-full lg:w-[45%]">
            <div className="relative">
              <div className="w-full h-[350px] sm:h-[420px] lg:h-[550px] rounded-2xl overflow-hidden shadow-[0_8px_40px_rgba(0,0,0,0.08)]">
                <img
                  src="https://public.readdy.ai/ai/img_res/0cf18079d63c15f8d9373bcf3fa7865c.jpg"
                  alt="Visione della città sostenibile del futuro"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Floating Quote */}
              <div className="absolute -right-2 md:-right-8 bottom-8 md:bottom-16 bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-[0_8px_32px_rgba(0,0,0,0.08)] max-w-[220px] md:max-w-[280px]">
                <i className="ri-double-quotes-l text-[#6b8e7f] text-xl md:text-2xl mb-2 md:mb-3 block"></i>
                <p className="font-playfair italic text-[13px] md:text-[16px] text-[#2d3e40] leading-[1.6]">
                  Il miglior momento per piantare un albero era vent&apos;anni fa. Il secondo miglior momento è adesso.
                </p>
                <span className="font-montserrat text-[10px] md:text-[11px] text-[#6b8e7f] uppercase tracking-[1px] mt-2 md:mt-3 block">
                  — Proverbio Cinese
                </span>
              </div>
            </div>
          </div>

          {/* Right: Vision Text */}
          <div className="w-full lg:w-[55%] pt-0 lg:pt-4">
            <h3 className="font-playfair text-[24px] md:text-[32px] font-bold text-[#2d3e40] leading-[1.3] mb-4 md:mb-6">
              Un Mondo dove l&apos;Architettura<br className="hidden md:block" /> Guarisce la Terra
            </h3>
            <p className="font-montserrat text-[13px] md:text-[15px] leading-[1.9] md:leading-[2] text-[#2d3e40]/70 mb-4 md:mb-6">
              Vedo un futuro in cui ogni edificio sarà un <strong className="text-[#2d3e40] font-semibold">organismo rigenerativo</strong> — non solo neutro, ma attivamente benefico per il pianeta. Un futuro dove le nostre case purificheranno l&apos;aria che respiriamo, dove i nostri uffici produrranno l&apos;energia che consumiamo, dove le nostre scuole insegneranno ai bambini il valore della simbiosi con la natura semplicemente esistendo.
            </p>
            <p className="font-montserrat text-[13px] md:text-[15px] leading-[1.9] md:leading-[2] text-[#2d3e40]/70 mb-4 md:mb-6">
              Il cambiamento climatico non è una minaccia lontana — è la realtà di oggi. Ma io credo che l&apos;architettura possa essere la nostra risposta più potente. Ogni metro quadrato costruito con <strong className="text-[#6b8e7f] font-semibold">materiali naturali</strong>, ogni tetto trasformato in giardino, ogni facciata che cattura CO₂ è un passo verso un pianeta che può respirare ancora.
            </p>
            <p className="font-montserrat text-[13px] md:text-[15px] leading-[1.9] md:leading-[2] text-[#2d3e40]/70 mb-8 md:mb-10">
              La <strong className="text-[#6b8e7f] font-semibold">sostenibilità</strong> non è un costo aggiuntivo — è un investimento nel futuro dei nostri figli. Ogni progetto che realizzo è una promessa: dimostrare che bellezza, comfort e rispetto per l&apos;ambiente possono coesistere in perfetta armonia. Il futuro dell&apos;edilizia è <strong className="text-[#2d3e40] font-semibold">bio-rigenerativo</strong>, e quel futuro inizia oggi, in ogni cantiere, in ogni scelta progettuale.
            </p>

            {/* Timeline Milestones */}
            <div className="border-l-2 border-[#6b8e7f]/20 pl-6 md:pl-8 space-y-5 md:space-y-6">
              {milestones.map((m, i) => (
                <div key={i} className="relative">
                  <div className="absolute -left-[33px] md:-left-[41px] top-1 w-4 h-4 rounded-full bg-[#6b8e7f] border-4 border-[#f5f2ee]"></div>
                  <span className="font-playfair text-[18px] md:text-[20px] font-bold text-[#6b8e7f]">{m.year}</span>
                  <p className="font-montserrat text-[12px] md:text-[14px] text-[#2d3e40]/65 mt-1 leading-[1.6]">{m.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Vision Points Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {visionPoints.map((point, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl p-6 md:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.08)] transition-all duration-500 group"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-[#6b8e7f]/10 flex items-center justify-center mb-4 md:mb-6 group-hover:bg-[#6b8e7f] transition-colors duration-400">
                <i className={`${point.icon} text-[#6b8e7f] text-xl md:text-2xl group-hover:text-white transition-colors duration-400`}></i>
              </div>
              <h3 className="font-playfair text-[18px] md:text-[20px] font-bold text-[#2d3e40] mb-2 md:mb-3 group-hover:text-[#6b8e7f] transition-colors duration-400">
                {point.title}
              </h3>
              <p className="font-montserrat text-[12px] md:text-[13px] text-[#2d3e40]/60 leading-[1.8]">
                {point.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom Statement */}
        <div className="mt-12 md:mt-20 text-center">
          <div className="inline-flex items-center gap-3 md:gap-4 bg-[#2d3e40] rounded-xl md:rounded-2xl px-6 md:px-12 py-5 md:py-8 max-w-full">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#6b8e7f]/20 flex items-center justify-center shrink-0">
              <i className="ri-earth-line text-[#8fb5a3] text-lg md:text-xl"></i>
            </div>
            <p className="font-playfair text-[15px] md:text-[22px] text-white leading-[1.4] text-left">
              &ldquo;Non ereditiamo la terra dai nostri padri, la prendiamo in prestito dai nostri figli.&rdquo;
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
